import { format } from 'date-fns';
import { SavedFilters } from '@features/toolbarFilter/types';
import { IntegrationName, type TicketsPayload } from '../../interfaces';
import { normalizeStoredFilters } from '../filters';
import type { FiltersForm } from '../filters/types';

type Payload = {
  sort: {
    column?: string;
    order?: 'asc' | 'desc' | null | undefined;
  };
};

export const formatPayload = (
  table: {
    page: number;
    per_page: number;
    search: string;
    sorting: {
      field: string;
      sort: 'asc' | 'desc' | null | undefined;
    }[];
  },
  filters: FiltersForm,
  savedFilters?: SavedFilters
) => {
  const { page, per_page, search } = table;

  const nowFilterJsonString = JSON.stringify(normalizeStoredFilters(filters));

  const isUsedSavedFilter = savedFilters?.find(({ filtersJsonString }) => {
    try {
      return (
        JSON.stringify(normalizeStoredFilters(JSON.parse(filtersJsonString))) ===
        nowFilterJsonString
      );
    } catch {
      return false;
    }
  });

  type TicketObject = NonNullable<TicketsPayload['objects']>[number];
  const objectGroups: TicketObject[][] = [];

  if (filters.objects.length > 0) {
    const objects = filters.objects.map((id) => ({
      id,
      type: 'complex',
    }));

    objectGroups.push(objects);
  }

  if (filters.houses.length > 0) {
    const houses = filters.houses.map(({ id }) => ({
      id,
      type: 'building',
    }));

    objectGroups.push(houses);
  }

  if (filters.apartments.length > 0) {
    const apartments = filters.apartments.map(({ id }) => ({
      id,
      type: 'apartment',
    }));

    objectGroups.push(apartments);
  }

  if (filters.property.length > 0) {
    const property = filters.property.map((id) => ({
      id,
      type: 'property',
    }));

    objectGroups.push(property);
  }

  const objectsForFilter = objectGroups[objectGroups.length - 1] ?? [];

  const payload: TicketsPayload & Payload = {
    page,
    per_page,
    text: search,
    sort: {},
    integrations: [],
    using_favourite_filter: !!isUsedSavedFilter,
  };

  const [createdAtFrom, createdAtTo] = filters.periodOfCreation;

  if (createdAtFrom) {
    payload.created_at_from = format(createdAtFrom, 'yyyy-MM-dd') + ' 00:00:00';
  }

  if (createdAtTo) {
    payload.created_at_to = format(createdAtTo, 'yyyy-MM-dd') + ' 23:59:59';
  }

  if (filters.statuses.length > 0) {
    payload.statuses = filters.statuses.map(({ id }) => id);
  }

  if (filters.isSupportActive) {
    payload.is_support_active = Number(filters.isSupportActive);
  }

  if (filters.priorities.length > 0) {
    payload.priorities = filters.priorities.map(({ id }) => id);
  }

  if (filters.classes.length > 0) {
    payload.classes = filters.classes.map(({ id }) => id);
  }

  if (filters.types.length > 0) {
    payload.types = filters.types.map(Number);
  }

  if (filters.payment !== null) {
    payload.paid = Boolean(Number(filters.payment));
  }

  if (filters.invoiceStatus) {
    payload.invoice_status = filters.invoiceStatus.id;
  }

  if (filters.stuffWriteOffNeeded !== null) {
    payload.stuff_write_off_needed = Number(filters.stuffWriteOffNeeded);
  }

  if (filters.expired !== null) {
    payload.expired = Number(filters.expired);
  }

  if (filters.assignees.length > 0) {
    payload.assignees = filters.assignees.map(({ id }) => id);
  }

  if (filters.rates.length > 0) {
    payload.rates = filters.rates.map(({ id }) => id);
  }

  if (filters.initiators.length > 0) {
    payload.initiators = filters.initiators.map(({ id }) => id);
  }

  payload.objects = objectsForFilter;

  if (filters.isUnreadMessage) {
    payload.has_unread_messages = Number(filters.isUnreadMessage);
  }

  if (filters.hasRelated) {
    payload.has_related = filters.hasRelated;
  }

  if (filters.isTehzor) {
    payload.integrations.push(IntegrationName.Tehzor);
  }

  if (table.sorting.length > 0) {
    const sorting = table.sorting[0];

    payload.sort.column = sorting.field;
    payload.sort.order = sorting.sort;
  }

  return payload;
};
